import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  ContextoTemaReact,
  type ContextoTema,
  type Marca,
  type PreferenciaTema,
  type Tema,
} from './contexto'

const CHAVE_TEMA = 'riodg.tema'
const CHAVE_MARCA = 'riodg.marca'

/**
 * Aplica tema e marca no elemento raiz.
 *
 * A escolha vira o atributo `data-tema`; a ausência dele é o que faz a
 * media query `prefers-color-scheme` valer (ver `semantico.css`). Por isso
 * "seguir o sistema" REMOVE o atributo em vez de gravar um valor — gravar
 * congelaria a preferência no que estava valendo no momento da escolha.
 */
function aplicar(preferencia: PreferenciaTema, marca: Marca) {
  const raiz = document.documentElement

  if (preferencia === 'sistema') raiz.removeAttribute('data-tema')
  else raiz.setAttribute('data-tema', preferencia)

  raiz.setAttribute('data-marca', marca)
}

function lerPreferencia(): PreferenciaTema {
  const v = localStorage.getItem(CHAVE_TEMA)
  return v === 'claro' || v === 'escuro' || v === 'sistema' ? v : 'sistema'
}

function lerMarca(): Marca {
  const v = localStorage.getItem(CHAVE_MARCA)
  return v === 'digiorgio' || v === 'letraseversos' || v === 'interno' ? v : 'digiorgio'
}

function sistemaEscuro() {
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

export function ProvedorTema({ children }: { children: ReactNode }) {
  const [preferencia, setPreferencia] = useState<PreferenciaTema>(lerPreferencia)
  const [marca, setMarca] = useState<Marca>(lerMarca)
  const [sistema, setSistema] = useState<Tema>(() => (sistemaEscuro() ? 'escuro' : 'claro'))

  // A preferência do sistema pode mudar com a aplicação aberta — trocar o
  // tema do sistema operacional precisa refletir sem recarregar (UI-07).
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const aoMudar = (e: MediaQueryListEvent) => setSistema(e.matches ? 'escuro' : 'claro')
    mq.addEventListener('change', aoMudar)
    return () => mq.removeEventListener('change', aoMudar)
  }, [])

  useEffect(() => aplicar(preferencia, marca), [preferencia, marca])

  const definirPreferencia = useCallback((p: PreferenciaTema) => {
    localStorage.setItem(CHAVE_TEMA, p)
    setPreferencia(p)
  }, [])

  const definirMarca = useCallback((m: Marca) => {
    localStorage.setItem(CHAVE_MARCA, m)
    setMarca(m)
  }, [])

  const valor = useMemo<ContextoTema>(
    () => ({
      preferencia,
      temaEfetivo: preferencia === 'sistema' ? sistema : preferencia,
      marca,
      definirPreferencia,
      definirMarca,
    }),
    [preferencia, sistema, marca, definirPreferencia, definirMarca],
  )

  return <ContextoTemaReact.Provider value={valor}>{children}</ContextoTemaReact.Provider>
}
